import { Button, Input, InputOnChangeData, MessageBar, Spinner } from '@fluentui/react-components';
import React from 'react';
import { Form, useNavigate } from 'react-router-dom';
import { useResolveDomainQuery, useStorefrontMultiLoginMutation } from '../Api/Api';
import { useLocalizationUnauthenticated } from '../Components/Helpers/useLocalization';

const StorefrontGenericLoginPage: React.FC = () => {
    const L = useLocalizationUnauthenticated();
    const nav = useNavigate();
    const message = new URLSearchParams(window.location.search).get('message');
    const redirect = new URLSearchParams(window.location.search).get('redirect');
    // Custom domains with a default storefront skip this generic page entirely: resolve the hostname
    // and hand off to the storefront-scoped login, where guest auto-login (loginMode 'guest') can take
    // the shopper straight in. A 404 means "no default here" and the generic form renders as always.
    const resolvedDomain = useResolveDomainQuery(window.location.hostname);
    React.useEffect(() => {
        if (!resolvedDomain.data?.organization || !resolvedDomain.data.storefront) return;
        nav(`/${resolvedDomain.data.organization}/${resolvedDomain.data.storefront}/login${window.location.search}`, { replace: true });
    }, [resolvedDomain.data, nav]);
    const [login] = useStorefrontMultiLoginMutation();
    const [loggingIn, setLoggingIn] = React.useState<boolean>(false);
    const [emailAddress, setEmailAddress] = React.useState<string>('');
    const [loginResult, setLoginResult] = React.useState<string | undefined>();

    const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setEmailAddress(data.value);
    }

    const onLogin = async () => {
        setLoggingIn(true);
        const result = await login({ emailAddress: emailAddress.toLowerCase(), redirect: redirect ?? '' });
        setLoggingIn(false);
        setLoginResult(result.data?.message);
    }

    const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onLogin();
    }

    // Hold the generic form until domain resolution settles so custom-domain visitors don't see it
    // flash before the storefront-scoped redirect. Requires both fields — the redirect above won't
    // fire on an incomplete result, so gating on organization alone would spin forever.
    if (resolvedDomain.isLoading || (resolvedDomain.data?.organization && resolvedDomain.data.storefront)) {
        return (
            <div className='login-container'>
                <div className='login-box'>
                    <Spinner />
                </div>
            </div>
        );
    }

    return (
        <div className='login-container'>
            <div className='login-box'>
                {!loggingIn && !loginResult && <>
                    <h2>{L('localization_auth_generic_login_title')}</h2>
                    {message && <MessageBar style={{marginBottom: '1em'}}>{message}</MessageBar>}
                    <Form onSubmit={onFormSubmit}>
                        <Input placeholder={L('localization_auth_email_address')} onChange={onEmailChange} value={emailAddress} style={{width: '100%'}} />
                        <Button appearance='primary' onClick={onLogin} style={{width: '100%', marginTop: '1em'}}>{L('localization_auth_login_button')}</Button>
                    </Form>
                </>}
                {loggingIn && <Spinner label={L('localization_auth_logging_in')} />}
                {loginResult && <div>{loginResult}</div>}
            </div>
        </div>
    );
}

export default StorefrontGenericLoginPage;